/**
 * VideoEndpoint - Canvas routing for video nodes
 *
 * Handles canvas connections between video nodes.
 * Each VideoEndpoint can have a canvas (output) or receive from connected sources (input).
 */

import type { IEndpoint } from '@/runtime/IEndpoint';
import { type Endpoint, type DataTypeSpec, type DataFlow } from '@/nodes/Endpoint';
import type { Payload } from '@/graph/Payload';
import type { StoreKey } from '@runtime/RuntimeStore';
import type { INode } from '@nodes/INode';
import { getVideoEntry, registerVideoRenderer } from './videoRegistry';
import { EndpointWithContext } from '../Plugin';
import { PolylineRenderer } from '@/plugins/generative/TurtleNode/PolylineRenderer';
import { logger } from '@/logging';


export class VideoEndpoint implements IEndpoint {
  readonly instancePath: string;
  readonly id: string;
  readonly dataType = 'video' as DataTypeSpec;
  readonly dataFlow = 'internal' as DataFlow;
  readonly isInput: boolean;
  readonly isOutput: boolean;
  readonly node: INode;

  bitmap: ImageBitmap;


  // Connected targets (for outputs) - includes both Video and other endpoint types
  targetEndpoints: IEndpoint[] = [];

  // Subgraph mapping: exposed->internal (1-1 relationship)
  internalEndpoint: IEndpoint | null = null;

  // Renderer created for non-video sources (e.g. polylines)
  private polylineRenderer: PolylineRenderer | null = null;

  constructor(ctx: EndpointWithContext) {
    this.instancePath = ctx.instancePath;
    this.node = ctx.node;
    this.id = ctx.endpoint.id;
    this.isInput = ctx.endpoint.isInput ?? false;
    this.isOutput = ctx.endpoint.isOutput ?? false;
  }



  /** Key object for store access */
  get storeKey(): StoreKey {
    return {
      instancePath: this.instancePath,
      nodeId: this.node.id,
      endpointId: this.id
    };
  }


  receive(payload: Payload, source: IEndpoint): void {
    if (this.polylineRenderer && source.dataType === 'polylines') {
      this.polylineRenderer.setPolylines(payload.value ?? []);
    }
    this.node.onInternalChanged(payload, this.id, source);
  }

  getValue(): Payload {
    return { value: null };
  }

  setValue(_payload: Payload, _source?: IEndpoint): void {

  }

  connectInternal(internal: IEndpoint): void {
    this.internalEndpoint = internal;
  }

  connectTo(target: IEndpoint): void {
    if (!this.targetEndpoints.includes(target)) {
      this.targetEndpoints.push(target);
    }

    // When connecting to another VideoEndpoint (e.g., canvas.videoIn),
    // register the source's renderer with the target canvas
    if (target instanceof VideoEndpoint) {
      this.setupVideoToVideoConnection(target);
    }
  }

  private setupVideoToVideoConnection(target: VideoEndpoint, retryCount = 0): void {
    // Traverse internalEndpoint chain to find the actual source
    let sourceEp: VideoEndpoint = this;
    while (sourceEp.internalEndpoint instanceof VideoEndpoint) {
      sourceEp = sourceEp.internalEndpoint;
    }

    // Look up the renderer from the video registry
    const entry = getVideoEntry(sourceEp.instancePath, sourceEp.node.id);
    const renderer = entry?.getRenderer();

    if (renderer) {
      // Register the source's renderer for the target canvas
      registerVideoRenderer(target.instancePath, target.node.id, renderer);
    } else if (retryCount < 10) {
      // Renderer not ready yet, retry
      setTimeout(() => this.setupVideoToVideoConnection(target, retryCount + 1), 100);
    }
  }

  disconnectFrom(target: IEndpoint): void {

  }

  dispose(): void {

  }

  setConnectionDisabled(target: IEndpoint, disabled: boolean): void {

  }

  onConnectedFrom(source: IEndpoint): void {
    if (source.dataType === 'polylines') {
      logger.trace('VideoEndpoint', `creating PolylineRenderer for ${this.instancePath}.${this.node.id}`);
      this.polylineRenderer = new PolylineRenderer();
      registerVideoRenderer(this.instancePath, this.node.id, this.polylineRenderer);
      // connectTo already pushed the initial value before this callback — forward it now
      const current = source.getValue();
      if (current?.value) {
        this.polylineRenderer.setPolylines(current.value);
      }
    }
  }
}
